import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API_URL } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  FolderOpen, FileText, Download, Trash2, Upload, LogOut, Loader2,
  ChevronDown, ChevronRight, Search, ShieldCheck, RefreshCw, Ship,
} from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_65c103ce-390f-4bae-b6d5-2798d8bc6281/artifacts/6in60tne_image.png";

const TAG_LABELS = {
  BL: "Bill of Lading",
  Invoice: "Commercial Invoice",
  PackingList: "Packing List",
  CustomsDoc: "Customs Document",
  Delivery: "Delivery Note",
  POD: "Proof of Delivery",
  CMR: "CMR",
  Other: "Other",
};
const UPLOAD_TAGS = ["CustomsDoc", "Invoice", "PackingList", "BL", "Delivery", "POD", "CMR", "Other"];

function fmtSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export default function PartnerPortalPage() {
  const { user, token, logout } = useAuth();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openPf, setOpenPf] = useState(null);
  const [docs, setDocs] = useState({}); // pf_id -> {loading, documents}
  const [uploadTag, setUploadTag] = useState("CustomsDoc");
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileRefs = useRef({});

  const headers = { Authorization: `Bearer ${token}` };

  const fetchFolders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/partner-portal/my-folders`, { headers });
      setFolders(data.folders || []);
    } catch (e) {
      toast.error("Failed to load your folders");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  const loadDocs = async (pf) => {
    setDocs((d) => ({ ...d, [pf.id]: { loading: true, documents: [] } }));
    try {
      const { data } = await axios.get(`${API_URL}/partner-portal/folders/${pf.id}/documents`, { headers });
      setDocs((d) => ({ ...d, [pf.id]: { loading: false, documents: data.documents || [] } }));
    } catch (e) {
      setDocs((d) => ({ ...d, [pf.id]: { loading: false, documents: [] } }));
      toast.error("Failed to load documents");
    }
  };

  const toggle = (pf) => {
    if (openPf === pf.id) { setOpenPf(null); return; }
    setOpenPf(pf.id);
    if (!docs[pf.id]) loadDocs(pf);
  };

  const download = async (pf, doc) => {
    try {
      const res = await axios.get(
        `${API_URL}/partner-portal/folders/${pf.id}/documents/${doc.id}/download`,
        { headers, responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const doDelete = async (pf, doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This only removes your own upload.`)) return;
    try {
      await axios.delete(`${API_URL}/partner-portal/folders/${pf.id}/documents/${doc.id}`, { headers });
      toast.success("Document deleted");
      loadDocs(pf);
      fetchFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const doUpload = async (pf, file) => {
    if (!file) return;
    setUploadingFor(pf.id);
    const form = new FormData();
    form.append("file", file);
    form.append("tag", uploadTag);
    try {
      await axios.post(`${API_URL}/partner-portal/folders/${pf.id}/documents`, form, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      loadDocs(pf);
      fetchFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingFor(null);
      if (fileRefs.current[pf.id]) fileRefs.current[pf.id].value = "";
    }
  };

  const filtered = folders.filter((f) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (f.pf_number || "").toLowerCase().includes(q) ||
      (f.description || "").toLowerCase().includes(q) ||
      (f.customer || "").toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-portal">
      {/* Header */}
      <header className="bg-[#003366] text-white sticky top-0 z-20 shadow-lg">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Europacific" className="h-9 w-9 rounded-full bg-white/10 p-1" />
            <div>
              <div className="text-base font-semibold leading-tight">
                <span className="font-light italic">my</span>Europacific
              </div>
              <div className="text-[11px] text-cyan-200 flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" /> Partner Document Repository
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium">{user?.full_name}</div>
              <div className="text-[11px] text-white/60">{user?.email}</div>
            </div>
            <Button
              variant="ghost" size="sm"
              className="text-white hover:bg-white/10"
              onClick={logout}
              data-testid="partner-logout-btn"
            >
              <LogOut className="h-4 w-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <FolderOpen className="h-6 w-6 text-[#003366]" /> Shared Folders
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Documents for the shipments shared with you. View, download and upload files.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search PF / customer…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 w-56 bg-white"
                data-testid="partner-search-input"
              />
            </div>
            <Button variant="outline" size="icon" onClick={fetchFolders} data-testid="partner-refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading your folders…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-xl border border-dashed border-slate-200" data-testid="partner-empty">
            <FolderOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No folders shared with you yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Once Europacific shares a shipment with you, it will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((pf) => {
              const isOpen = openPf === pf.id;
              const pfDocs = docs[pf.id];
              return (
                <div key={pf.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-testid={`partner-folder-${pf.pf_number}`}>
                  <button
                    onClick={() => toggle(pf)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
                    data-testid={`partner-folder-toggle-${pf.pf_number}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {isOpen ? <ChevronDown className="h-5 w-5 text-[#003366] shrink-0" /> : <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />}
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-800 flex items-center gap-2">
                          {pf.pf_number}
                          <span className="text-xs font-normal bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{pf.status || "—"}</span>
                        </div>
                        <div className="text-xs text-slate-500 truncate flex items-center gap-2">
                          {pf.customer && <span>{pf.customer}</span>}
                          {(pf.pol || pf.pod) && (
                            <span className="flex items-center gap-1"><Ship className="h-3 w-3" />{pf.pol || "?"} → {pf.pod || "?"}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-[#003366] bg-cyan-50 px-2.5 py-1 rounded-full shrink-0">
                      {pf.document_count} docs
                    </span>
                  </button>

                  {isOpen && (
                    <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/50">
                      {/* Upload bar */}
                      <div className="flex items-center gap-2 mb-3 flex-wrap">
                        <select
                          value={uploadTag}
                          onChange={(e) => setUploadTag(e.target.value)}
                          className="text-sm border border-slate-200 rounded-md px-2 py-2 bg-white"
                          data-testid={`partner-upload-tag-${pf.pf_number}`}
                        >
                          {UPLOAD_TAGS.map((t) => <option key={t} value={t}>{TAG_LABELS[t]}</option>)}
                        </select>
                        <input
                          type="file"
                          ref={(el) => (fileRefs.current[pf.id] = el)}
                          className="hidden"
                          onChange={(e) => doUpload(pf, e.target.files?.[0])}
                          data-testid={`partner-file-input-${pf.pf_number}`}
                        />
                        <Button
                          size="sm"
                          onClick={() => fileRefs.current[pf.id]?.click()}
                          disabled={uploadingFor === pf.id}
                          data-testid={`partner-upload-btn-${pf.pf_number}`}
                        >
                          {uploadingFor === pf.id ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                          Upload document
                        </Button>
                      </div>

                      {pfDocs?.loading ? (
                        <div className="flex items-center py-6 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading documents…</div>
                      ) : (pfDocs?.documents || []).length === 0 ? (
                        <p className="text-sm text-slate-400 py-4 text-center">No documents in this folder yet.</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(pfDocs?.documents || []).map((d) => (
                            <div key={d.id} className="flex items-center justify-between bg-white rounded-lg border border-slate-100 px-3 py-2" data-testid={`partner-doc-${d.id}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                <FileText className="h-4 w-4 text-red-500 shrink-0" />
                                <div className="min-w-0">
                                  <div className="text-sm text-slate-700 truncate">{d.filename}</div>
                                  <div className="text-[11px] text-slate-400 flex items-center gap-2">
                                    <span className="bg-slate-100 px-1.5 rounded">{TAG_LABELS[d.tag] || d.tag}</span>
                                    <span>{fmtSize(d.file_size)}</span>
                                    {d.source === "partner" && <span className="text-cyan-600">• uploaded by you</span>}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => download(pf, d)} title="Download" data-testid={`partner-download-${d.id}`}>
                                  <Download className="h-4 w-4 text-[#003366]" />
                                </Button>
                                {d.can_delete && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => doDelete(pf, d)} title="Delete your upload" data-testid={`partner-delete-${d.id}`}>
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export { PartnerPortalPage };
